/**
 * DiscountComponent handles inline discount code application in cart drawer and main cart.
 * @class DiscountComponent
 * @extends HTMLElement
 */
class DiscountComponent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.messageTimeout = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.sectionId = this.dataset.sectionId;
    this.input = this.querySelector('[name="discount-code"]');
    this.button = this.querySelector('[discount-submit]');
    this.codesContainer = this.querySelector('[manual-discount-code]');

    if (this.button && this.input) {
      this.init();
    }

    this.addEventListener(
      _EVENT_HELPER.removeDiscountCode,
      this.handleRemoveDiscount.bind(this)
    );
  }

  init() {
    this.button.addEventListener('click', this.handleSubmit.bind(this));
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit(e);
      }
    });
  }

  getExistingCodes() {
    return [...this.querySelectorAll('[manual-discount-code] [data-code]')]
      .map(el => el.dataset.code);
  }

  async handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const code = this.input?.value?.trim();
    if (!code) return;

    const existingCodes = this.getExistingCodes();
    const upperCodes = existingCodes.map(c => c.toUpperCase());

    if (upperCodes.includes(code.toUpperCase())) return;

    const discountCode = existingCodes.length
      ? `${code},${existingCodes.join(',')}`
      : code;

    this.setLoading(true);

    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCode,
          sections: 'manual-discount-codes,cart-drawer,main-cart'
        })
      });

      const data = await response.json();
      this.input.value = '';

      const hasInvalidCode = this.checkForInvalidCode(data.discount_codes, existingCodes);

      if (hasInvalidCode) {
        this.showMessage('error');
      } else {
        const isShippingOnly = this.checkForShippingOnlyCode(data.sections, existingCodes);

        if (isShippingOnly) {
          this.showMessage('shipping-error');
        } else {
          this.showMessage('success');
          this.updateCodesDisplay(data.sections);
          this.triggerCartUpdate();
        }
      }
    } catch (error) {
      this.showMessage('error');
    } finally {
      this.setLoading(false);
    }
  }

  async handleRemoveDiscount(e) {
    if (e.detail.preventBubble) {
      e.preventDefault();
      e.stopPropagation();
    }

    const codeToRemove = e.detail.code;
    const existingCodes = this.getExistingCodes();
    const newCodes = existingCodes.filter(c => c !== codeToRemove);

    this.setLoading(true);

    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: newCodes.join(','),
          sections: 'manual-discount-codes,cart-drawer,main-cart'
        })
      });

      const data = await response.json();
      this.updateCodesDisplay(data.sections);
      this.triggerCartUpdate();
    } catch (error) {
      console.error('Error removing discount:', error);
    } finally {
      this.setLoading(false);
    }
  }

  checkForInvalidCode(discountCodes, existingCodes) {
    if (!discountCodes) return false;

    return discountCodes.some(discount => {
      const hasFalseKey = Object.values(discount).includes(false);
      const isNewCode = !existingCodes.includes(discount.code);
      return hasFalseKey && isNewCode;
    });
  }

  checkForShippingOnlyCode(sections, existingCodes) {
    if (!sections?.['manual-discount-codes']) return false;

    try {
      const doc = new DOMParser().parseFromString(
        sections['manual-discount-codes'],
        'text/html'
      );
      const newCodes = [...doc.querySelectorAll('[manual-discount-code] [data-code]')]
        .map(el => el.dataset.code);

      return newCodes.length === existingCodes.length &&
        newCodes.every(code => existingCodes.includes(code));
    } catch {
      return false;
    }
  }

  updateCodesDisplay(sections) {
    if (!sections?.['manual-discount-codes'] || !this.codesContainer) return;

    try {
      const doc = new DOMParser().parseFromString(
        sections['manual-discount-codes'],
        'text/html'
      );
      const newCodes = doc.querySelector('[manual-discount-code]');
      if (newCodes) {
        this.codesContainer.innerHTML = newCodes.innerHTML;
      }
    } catch (error) {
      console.error('Error updating codes display:', error);
    }
  }

  triggerCartUpdate() {
    if (typeof HELPER_UTIL !== 'undefined' && typeof _EVENT_HELPER !== 'undefined') {
      HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart);
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.button?.classList.add('disable');
    } else {
      this.button?.classList.remove('disable');
    }
  }

  showMessage(type) {
    this.hideAllMessages();

    const selector = type === 'shipping-error' ? '[discount-shipping-error]' : `[discount-${type}]`;
    const messageEl = this.querySelector(selector);

    if (messageEl) {
      messageEl.removeAttribute('hidden');
      this.messageTimeout = setTimeout(() => {
        messageEl.setAttribute('hidden', 'true');
      }, 3000);
    }
  }

  hideAllMessages() {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    this.querySelectorAll('[discount-error], [discount-success], [discount-shipping-error]')
      .forEach(el => el.setAttribute('hidden', 'true'));
  }
}

customElements.get('discount-component') || customElements.define('discount-component', DiscountComponent);

/**
 * RemoveDiscountPill handles removal of individual discount code badges.
 * @class RemoveDiscountPill
 * @extends HTMLElement
 */
class RemoveDiscountPill extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick() {
    const codeEl = this.closest('[data-code]');
    if (!codeEl) return;

    HELPER_UTIL.dispatchCustomEvent(
      _EVENT_HELPER.removeDiscountCode,
      this,
      {
        preventBubble: true,
        code: codeEl.dataset.code
      }
    );

    codeEl.remove();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick.bind(this));
  }
}

customElements.get('remove-discount-pill') ||
  customElements.define('remove-discount-pill', RemoveDiscountPill);
